import * as React from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    SelectInput,
    NumberInput,
    DateInput,
    useDefaultTitle,
    useEditContext,
    required,
    email,
    Toolbar,
    SaveButton,
    Button,
    useNotify,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Grid, Box, Typography, Avatar, IconButton } from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import { ROLE_CHOICES, STATUS_CHOICES, GENDER_CHOICES, VIP_CHOICES, TRIAL_CHOICES, SOURCE_CHOICES } from './constants';
import { useNavigate } from 'react-router-dom';
import { getAccessToken } from '../authProvider';
import { API_BASE_URL } from '../config';

export type UserEditConfig = {
    resource?: string;
    cancelPath?: string;
    mode?: 'default' | 'player' | 'club' | 'coach' | 'admin';
};

const UserTitle = () => {
    const { record } = useEditContext();
    const title = useDefaultTitle();
    return (
        <>
            <title>{`${title} - ${record?.name || 'User'}`}</title>
            <span>{record ? `User "${record.name}"` : ''}</span>
        </>
    );
};

const UserEditToolbar = ({ cancelPath }: { cancelPath: string }) => {
    const navigate = useNavigate();
    
    return (
        <Toolbar>
            <SaveButton />
            <Button 
                label="Cancel" 
                onClick={() => navigate(cancelPath)}
            />
        </Toolbar>
    );
};

const AvatarUpload = () => {
    const { record } = useEditContext();
    const { setValue } = useFormContext();
    const [accessToken, setAccessToken] = React.useState<string>('');
    const [uploading, setUploading] = React.useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const notify = useNotify();
    
    React.useEffect(() => {
        const fetchToken = async () => {
            const token = await getAccessToken();
            if (token) setAccessToken(token);
        };
        fetchToken();
    }, []);
    
    const avatarUrl = record?.picUrl
        ? `${API_BASE_URL}/r2/download${record.picUrl}?authorization=${accessToken}`
        : undefined;

    const handleAvatarClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            notify('Please select an image file', { type: 'error' });
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            notify('Image size should be less than 5MB', { type: 'error' });
            return;
        }

        setUploading(true);
        try {
            const token = await getAccessToken();
            const userId = record.id;
            
            const response = await fetch(`${API_BASE_URL}/r2/upload/avatar/${userId}?authorization=${token}`, {
                method: 'POST',
                headers: {
                    'Content-Type': file.type
                },
                body: file,
            });

            if (!response.ok) {
                throw new Error('Upload failed');
            }

            const result = await response.json();
            if (result.success && result.data) {
                // Update the record with new picUrl
                // Update form value to enable save button
                setValue('picUrl', result.data, { shouldDirty: true });
                record.picUrl = result.data;
                notify('Avatar uploaded successfully', { type: 'success' });
                // Force re-render
                setAccessToken(token || '');
            } else {
                throw new Error(result.message || 'Upload failed');
            }
        } catch (error) {
            notify('Failed to upload avatar', { type: 'error' });
            console.error('Upload error:', error);
        } finally {
            setUploading(false);
            // Clear the input value to allow uploading the same file again
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    return (
        <Grid size={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, position: 'relative' }}>
                <Box sx={{ position: 'relative' }}>
                    <Avatar
                        src={avatarUrl}
                        sx={{ width: 120, height: 120, cursor: 'pointer' }}
                        onClick={handleAvatarClick}
                    />
                    <IconButton
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'primary.dark',
                            },
                            width: 36,
                            height: 36,
                        }}
                        onClick={handleAvatarClick}
                        disabled={uploading}
                    >
                        <PhotoCamera />
                    </IconButton>
                </Box>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />
            </Box>
            {uploading && (
                <Typography variant="body2" align="center" color="text.secondary">
                    Uploading...
                </Typography>
            )}
        </Grid>
    );
};

const UserEditForm = ({
    cancelPath,
    mode,
}: {
    cancelPath: string;
    mode: NonNullable<UserEditConfig['mode']>;
}) => {
    return (
        <SimpleForm toolbar={<UserEditToolbar cancelPath={cancelPath} />}>
            <Grid container spacing={2} sx={{ width: '100%', maxWidth: 800 }}>
                {/* User Avatar */}
                <AvatarUpload />

                {/* Basic Information */}
                <Grid size={12}>
                    <Typography variant="h6" gutterBottom>
                        Basic Information
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextInput source="id" label="ID" disabled fullWidth />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextInput
                        source="email"
                        label="Email"
                        disabled
                        fullWidth
                        validate={[required(), email()]}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextInput
                        source="name"
                        label="Name"
                        fullWidth
                        validate={required()}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextInput source="phone" label="Phone" fullWidth />
                </Grid>

                {/* Status and Role */}
                <Grid size={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                        Status & Role
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <SelectInput
                        source="status"
                        label="Status"
                        fullWidth
                        choices={STATUS_CHOICES}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <SelectInput
                        source="role"
                        label="Role"
                        fullWidth
                        disabled
                        choices={ROLE_CHOICES}
                    />
                </Grid>

                {/* Personal Information */}
                <Grid size={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                        Personal Information
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <SelectInput
                        source="gender"
                        label="Gender"
                        fullWidth
                        choices={GENDER_CHOICES}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <DateInput source="birthDate" label="Birth Date" fullWidth />
                </Grid>

                <Grid size={12}>
                    <TextInput source="location" label="Location" fullWidth />
                </Grid>

                <Grid size={12}>
                    <TextInput
                        source="bio"
                        label="Bio"
                        fullWidth
                        multiline
                        rows={3}
                    />
                </Grid>

                {(mode === 'player' || mode === 'default') && (
                    <>
                        <Grid size={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                VIP & Membership
                            </Typography>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <SelectInput
                                source="trial"
                                label="Trial"
                                fullWidth
                                choices={TRIAL_CHOICES}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <SelectInput
                                source="vip"
                                label="VIP Status"
                                fullWidth
                                choices={VIP_CHOICES}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <DateInput
                                source="vipEndTime"
                                label="VIP End Date"
                                fullWidth
                                parse={(value: string) => {
                                    if (!value) return null;
                                    const date = new Date(value);
                                    if (isNaN(date.getTime())) return null;
                                    const daysSinceEpoch = Math.floor(date.getTime() / (1000 * 60 * 60 * 24));
                                    return daysSinceEpoch + 2440588;
                                }}
                                format={(value: any) => {
                                    if (typeof value === 'string') return value;
                                    if (!value || typeof value !== 'number' || isNaN(value) || value <= 0) return '';
                                    const daysSinceEpoch = value - 2440588;
                                    const milliseconds = daysSinceEpoch * 24 * 60 * 60 * 1000;
                                    const date = new Date(milliseconds);
                                    return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
                                }}
                            />
                        </Grid>
                    </>
                )}

                {mode === 'player' && (
                    <>
                        <Grid size={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                Player Profile
                            </Typography>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <NumberInput source="preferredFoot" label="Preferred Foot" fullWidth />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <NumberInput source="hightLight" label="Highlight" fullWidth />
                        </Grid>
                        <Grid size={12}>
                            <TextInput source="preferredPositions" label="Preferred Positions" fullWidth />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextInput source="heightUnit" label="Height Unit" fullWidth />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextInput source="weightUnit" label="Weight Unit" fullWidth />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextInput source="height" label="Height" fullWidth />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextInput source="weight" label="Weight" fullWidth />
                        </Grid>
                    </>
                )}

                {mode === 'club' && (
                    <>
                        <Grid size={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                Club Profile
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextInput source="clubName" label="Club Name" fullWidth />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextInput source="clubAddress" label="Club Address" fullWidth />
                        </Grid>
                        <Grid size={12}>
                            <TextInput source="clubPicUrl" label="Club PicUrl" fullWidth />
                        </Grid>
                    </>
                )}

                {/* System Information */}
                <Grid size={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                        System Information
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <NumberInput
                        source="objectId"
                        label="Object ID"
                        fullWidth
                        disabled
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <SelectInput
                        source="sourceId"
                        label="Registration Source"
                        fullWidth
                        disabled
                        choices={SOURCE_CHOICES}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextInput
                        source="createTime"
                        label="Created Time"
                        disabled
                        fullWidth
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextInput
                        source="updateTime"
                        label="Updated Time"
                        disabled
                        fullWidth
                    />
                </Grid>
            </Grid>
        </SimpleForm>
    );
};

export const createUserEdit = ({
    resource = 'admin/users',
    cancelPath = '/admin/users',
    mode = 'default',
}: UserEditConfig = {}) => {
    const UserEdit = () => {
        return (
            <Edit title={<UserTitle />} redirect="list" resource={resource}>
                <UserEditForm cancelPath={cancelPath} mode={mode} />
            </Edit>
        );
    };

    return UserEdit;
};

const UserEdit = createUserEdit();

export default UserEdit;
